import { useCallback, useEffect, useMemo, useRef, useState } from 'react';


const BASE = '/api';


let inFlight = 0;
const inFlightListeners = new Set<(count: number) => void>();

function settle(delta: number): void {
  inFlight += delta;
  for (const listener of inFlightListeners) listener(inFlight);
}

export function useApiBusy(): boolean {
  const [count, setCount] = useState(inFlight);

  useEffect(() => {
    const listener = (next: number) => setCount(next);
    inFlightListeners.add(listener);
    setCount(inFlight);
    return () => {
      inFlightListeners.delete(listener);
    };
  }, []);

  return count > 0;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly hint?: string,
  ) {
    super(message);
  }

  get full(): string {
    return this.hint ? `${this.message} — ${this.hint}` : this.message;
  }
}

export async function apiGet<T>(
  path: string,
  params: Record<string, string | undefined> = {},
  signal?: AbortSignal,
): Promise<T> {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') query.set(key, value);
  }
  const suffix = query.toString();

  settle(+1);
  try {
    const response = await fetch(`${BASE}${path}${suffix ? `?${suffix}` : ''}`, {
      signal,
      headers: { accept: 'application/json' },
      credentials: 'include',
    });

    if (!response.ok) {
      const body = (await response.json().catch(() => null)) as {
        error?: string;
        hint?: string;
      } | null;
      throw new ApiError(
        response.status,
        body?.error ?? `${response.status} ${response.statusText}`,
        body?.hint,
      );
    }
    return (await response.json()) as T;
  } finally {
    settle(-1);
  }
}

export async function apiSend<T>(
  method: 'POST' | 'PATCH' | 'DELETE',
  path: string,
  body?: unknown,
  params: Record<string, string | undefined> = {},
): Promise<T> {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') query.set(key, value);
  }
  const suffix = query.toString();

  settle(+1);
  try {
    const response = await fetch(`${BASE}${path}${suffix ? `?${suffix}` : ''}`, {
      method,
      headers:
        body === undefined
          ? { accept: 'application/json' }
          : {
              accept: 'application/json',
              'content-type': 'application/json',
            },
      body: body === undefined ? undefined : JSON.stringify(body),
      credentials: 'include',
    });

    if (!response.ok) {
      const payload = (await response.json().catch(() => null)) as {
        error?: string;
        hint?: string;
      } | null;
      throw new ApiError(
        response.status,
        payload?.error ?? `${response.status} ${response.statusText}`,
        payload?.hint,
      );
    }
    return (await response.json()) as T;
  } finally {
    settle(-1);
  }
}

export interface Resource<T> {
  data: T | undefined;
  error: string | undefined;
  status: number | undefined;
  loading: boolean;
  reload: () => void;
}

export function useResource<T>(
  path: string | null,
  params: Record<string, string | undefined> = {},
): Resource<T> {
  const [data, setData] = useState<T>();
  const [error, setError] = useState<string>();
  const [status, setStatus] = useState<number>();
  const [loading, setLoading] = useState(path !== null);
  const [nonce, setNonce] = useState(0);
  const key = JSON.stringify(params);
  const latest = useRef(0);

  useEffect(() => {
    if (path === null) {
      setLoading(false);
      return;
    }
    const attempt = ++latest.current;
    const controller = new AbortController();
    setLoading(true);
    setError(undefined);
    setStatus(undefined);

    apiGet<T>(path, JSON.parse(key) as Record<string, string | undefined>, controller.signal)
      .then((result) => {
        if (attempt !== latest.current) return;
        setData(result);
        setLoading(false);
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted || attempt !== latest.current) return;
        setError(
          cause instanceof ApiError
            ? cause.message
            : 'Cannot reach the API. Start it with `pnpm api`.',
        );
        setStatus(cause instanceof ApiError ? cause.status : undefined);
        setLoading(false);
      });

    return () => controller.abort();
  }, [path, key, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  return { data, error, status, loading, reload };
}


export interface Session {
  id: string;
  username: string;
  email: string;
  fullName: string;
  initials: string;
  jobTitle: string | null;
  isSuperAdmin: boolean;
  roleCode: string;
  roleName: string;
  approvalLimitBase: string | null;
  canSelfApprove: boolean;
  permissions: string[];
  branches: Array<{ code: string; name: string; isDefault: boolean }>;
  viewingAs: { realEmail: string } | null;
  mustChangePassword: boolean;
}

export interface ApiAccount {
  code: string;
  name: string;
  shortName: string | null;
  type: 'ASSET' | 'LIABILITY' | 'EQUITY' | 'INCOME' | 'EXPENSE';
  normalBalance: 'DR' | 'CR';
  parentCode: string | null;
  depth: number;
  level: number;
  isLeaf: boolean;
  isControl: boolean;
  controlKind: string | null;
  allowDirectPosting: boolean;
  isContra: boolean;
  isSystem: boolean;
  fcyCurrency: string | null;
  requiresParty: boolean;
  statementGroup: string | null;
  cashFlowClass: string | null;
  isBank: boolean;
  isActive: boolean;
  notes: string | null;
  childCount: number;
  hasPostings: boolean;
  subLedgerKind: 'salesperson' | 'party' | null;
  subLedgerCode: string | null;
  subLedgerName: string | null;
  balanceBase: string;
  movements: number;
}

export interface AccountWriteResult {
  code: string;
  name: string;
  message: string;
}

export interface LedgerRow {
  date: string;
  voucherNo: string;
  voucherType: string;
  lineNo: number;
  status: string;
  partyName: string | null;
  particulars: string;
  debitBase: string;
  creditBase: string;
  signedBase: string;
  currency: string;
  debit: string;
  credit: string;
}

export interface LedgerResponse {
  account: {
    id: string;
    code: string;
    name: string;
    normalBalance: 'DR' | 'CR';
    type: string;
    isControl: boolean;
  };
  opening: string;
  rows: LedgerRow[];
}

export interface TrialBalanceRow {
  code: string;
  name: string;
  type: ApiAccount['type'];
  normalBalance: 'DR' | 'CR';
  depth: number;
  openingDebitBase: string;
  openingCreditBase: string;
  debitBase: string;
  creditBase: string;
  closingDebitBase: string;
  closingCreditBase: string;
  closingSide: string;
}

export interface TrialBalanceResult {
  from: string | null;
  to: string | null;
  rows: TrialBalanceRow[];
}

export interface StatementRow {
  code: string;
  name: string;
  type: ApiAccount['type'];
  statementGroup: string | null;
  isContra: boolean;
  amountBase: string;
  isResult?: boolean;
}

export interface ProfitAndLossResult {
  from: string | null;
  to: string | null;
  rows: StatementRow[];
}

export interface BalanceSheetResult {
  asOn: string | null;
  rows: StatementRow[];
}

export interface CashPositionRow {
  code: string;
  name: string;
  kind: 'CASH' | 'BANK';
  balanceBase: string;
  lastMovement: string | null;
}

export interface AgeingRow {
  partyCode: string;
  partyName: string;
  current: string;
  d30: string;
  d60: string;
  d90: string;
  d90plus: string;
  total: string;
}

export interface ApAgeingRow extends AgeingRow {
  origin: string;
}

export interface ApiParty {
  code: string;
  name: string;
  kind: string;
  isCustomer: boolean;
  isSupplier: boolean;
  isEmployee: boolean;
  isSubAgent: boolean;
  ntn: string | null;
  strn: string | null;
  isFiler: boolean;
  receivableAccount: string | null;
  payableAccount: string | null;
}


export interface VoucherListRow {
  id: string;
  voucherNo: string;
  voucherDate: string;
  status: string;
  narration: string | null;
  totalBase: string;
  lineCount: number;
  partyCode: string | null;
  partyName: string | null;
  isReversed: boolean;
}

export interface VoucherDetailLine {
  lineNo: number;
  accountCode: string;
  accountName: string;
  parentCode: string | null;
  parentName: string | null;
  side: 'Dr' | 'Cr';
  amount: string;
  debit: string;
  credit: string;
  debitBase: string;
  creditBase: string;
  partyCode: string | null;
  partyName: string | null;
  narration: string | null;
}

export interface AuditStamp {
  at: string | null;
  by: string | null;
}

export interface VoucherDetail {
  id: string;
  voucherNo: string;
  voucherType: string;
  voucherDate: string;
  status: string;
  narration: string | null;
  currency: string;
  fxRate: string;
  partyCode: string | null;
  partyName: string | null;
  branchCode: string | null;
  branchName: string | null;
  periodName: string | null;
  totalDebitBase: string;
  totalCreditBase: string;
  lineCount: number;
  createdAt: string | null;
  createdByName: string | null;
  updatedAt: string | null;
  updatedByName: string | null;
  submittedAt: string | null;
  submittedByName: string | null;
  approvedAt: string | null;
  approvedByName: string | null;
  postedAt: string | null;
  postedByName: string | null;
  reversalReason: string | null;
  reversedByNo: string | null;
  reversalOfNo: string | null;
  lines: VoucherDetailLine[];
}

export interface SavedVoucher {
  id: string;
  voucherNo: string;
  type: string;
  voucherDate: string;
  narration: string | null;
  status: string;
  totalDebitBase: string;
  totalCreditBase: string;
  lineCount: number;
  postedAt: string | null;
}

export interface DeleteResult {
  action: 'DELETED' | 'REVERSED';
  voucherNo: string;
  reversalNo?: string;
  message: string;
}

export interface BulkRowResult {
  row: number;
  reference: string;
  ok: boolean;
  voucherNo?: string;
  status?: string;
  error?: string;
}

export interface BulkResult {
  total: number;
  succeeded: number;
  failed: number;
  results: BulkRowResult[];
}

export interface ApiContext {
  company: {
    code: string;
    name: string;
    legalName: string;
    tagline: string | null;
    brandMark: string | null;
    logoUrl: string | null;
    ntn: string | null;
    strn: string | null;
    cuin: string | null;
    printShowNtn: boolean;
    printShowStrn: boolean;
    printShowCuin: boolean;
    addressLine1: string | null;
    addressLine2: string | null;
    city: string | null;
    province: string | null;
    phone: string | null;
    email: string | null;
    website: string | null;
    reportFooter: string | null;
    baseCurrency: string;
    timezone: string;
    defaultUomCode: string | null;
  };
  fiscalYear: {
    code: string;
    name: string;
    startDate: string;
    endDate: string;
    status: string;
  } | null;
  periods: Array<{
    periodNo: number;
    name: string;
    startDate: string;
    endDate: string;
    status: string;
    isAdjustment: boolean;
  }>;
  currencies: Array<{
    code: string;
    name: string;
    symbol: string | null;
    decimals: number;
    isBase: boolean;
    rate: string | null;
  }>;
  uoms: Array<{ code: string; name: string; dimension: string; decimals: number }>;
  usdRate: string | null;
}


export interface SalesByZoneRow {
  zoneCode: string | null;
  zoneName: string;
  salespersonCode: string | null;
  salespersonName: string;
  invoices: number;
  qtyBase: string;
  netSales: string;
  cogs: string;
  margin: string;
  marginPct: string | null;
  commission: string;
  targetValue: string | null;
  achievementPct: string | null;
}

export interface GrossMarginRow {
  sku: string;
  productName: string;
  categoryName: string | null;
  qtyBase: string;
  netSales: string;
  cogs: string;
  costPerUnit: string | null;
  ratePerUnit: string | null;
  marginPerUnit: string | null;
  margin: string;
  marginPct: string | null;
}

export interface AuditEntityRef {
  entityTable: string;
  entityId: string | null;
  entityKey: string | null;
  entityName: string | null;
}

export interface AuditTrailRow {
  loggedAt: string;
  username: string;
  action: string;
  entityTable: string;
  entityId: string | null;
  entityKey: string | null;
  entityName: string | null;
  documentRef: string | null;
  amountBase: string | null;
  changedKeys: string[] | null;
  ipAddress: string | null;
  reason: string | null;
  changes: number;
  touched: AuditEntityRef[];
  touchedCount: number;
}


export interface DashboardKpi {
  key: 'cash' | 'receivables' | 'payables' | 'profit';
  label: string;
  valueBase: string;
  deltaPct: string | null;
  foot: string;
}

export interface DashboardResponse {
  asOf: string;
  fiscalYear: string | null;
  period: string | null;
  usdRate: string | null;
  kpis: DashboardKpi[];
  brokerage: {
    deal_value_mtd: string;
    deal_count_mtd: number;
    commission_mtd: string;
    commission_receivable: string;
    commission_invoice_count: number;
    unsettled: string;
  };
  trading: {
    net_sales_mtd: string;
    invoice_count_mtd: number;
    margin_mtd: string;
    stock_value: string;
    warehouse_count: number;
    in_transit: string;
    in_transit_lots: number;
    usd_payable: string;
  };
  cashBars: Array<{ week: string; receipts: string; payments: string }>;
  queue: {
    drafts: number;
    pending_approval: number;
    unmatched_bank: number;
    overdue_invoices: number;
    overdue_base: string;
    expiring_batches: number;
    unsettled_deals: number;
  };
  recent: Array<{
    voucherNo: string;
    voucherType: string;
    voucherDate: string;
    narration: string | null;
    partyName: string | null;
    totalBase: string;
    status: string;
  }>;
  trialBalance: { debits: string; credits: string; difference: string; unposted: number };
  stockAtRisk: Array<{
    sku: string;
    productName: string;
    batchNo: string;
    warehouseName: string;
    balanceQty: string;
    balanceValue: string;
    expiryDate: string | null;
    daysToExpiry: number | null;
    expiryStatus: string;
  }>;
}


export interface AdminUser {
  id: string;
  username: string;
  email: string;
  fullName: string;
  initials: string | null;
  jobTitle: string | null;
  phone: string | null;
  status: string;
  roleCode: string;
  roleName: string;
  rank: number;
  approvalLimitBase: string | null;
  inheritsScope: boolean;
  scopeLevel: string;
  scopeName: string;
  scopeCode: string | null;
  branchCode: string | null;
  branchName: string | null;
  lastLoginAt: string | null;
  permissionCount: number;
  reportCount: number;
  canManage: boolean;
  isSuperAdmin: boolean;
}

export interface AdminRole {
  id: string;
  code: string;
  name: string;
  description: string | null;
  rank: number;
  inheritsSubordinateScope: boolean;
  canSelfApprove: boolean;
  approvalLimitBase: string | null;
  isSystem: boolean;
  isActive: boolean;
  userCount: number;
  permissionCount: number;
  canManage: boolean;
}

export interface PermissionModule {
  code: string;
  groupName: string;
  name: string;
  sortOrder: number;
  allowedActions: string[];
}

export interface RolesResponse {
  roles: AdminRole[];
  modules: PermissionModule[];
  grants: Record<string, string[]>;
  callerRank: number;
  callerRoleName: string;
  hiddenModulesExist: boolean;
}

export interface AdminZone {
  id: string;
  code: string;
  name: string;
  parentCode: string | null;
  depth: number;
  level: number;
  path: string;
  managerId: string | null;
  managerName: string | null;
  isActive: boolean;
  childCount: number;
  userCount: number;
  customerCount: number;
  cityCount: number;
  cities: string[];
}

export interface AdminCity {
  id: string;
  code: string;
  name: string;
  province: string | null;
  zoneCode: string | null;
  zoneName: string | null;
  isActive: boolean;
  invoiceCount: number;
}

export interface AdminWarehouse {
  id: string;
  code: string;
  name: string;
  kind: string;
  branchCode: string | null;
  addressLine1: string | null;
  phone: string | null;
  inventoryAccount: string | null;
  inventoryAccountName: string | null;
  inTransitAccount: string | null;
  allowNegativeStock: boolean;
  isDefault: boolean;
  isActive: boolean;
  movementCount: number;
  documentCount: number;
}

export interface GeographyRefs {
  cities: AdminCity[];
  warehouses: AdminWarehouse[];
  branches: Array<{ code: string; name: string; isHeadOffice: boolean }>;
  warehouseKinds: string[];
  stockHead: { code: string; name: string } | null;
  can: {
    city: { create: boolean; edit: boolean; delete: boolean };
    warehouse: { view: boolean; create: boolean; edit: boolean; delete: boolean };
  };
}

export interface AdminCaller {
  email: string;
  rank: number;
  roleName: string;
  isSuperAdmin: boolean;
  canCreateUser: boolean;
  canEditUser: boolean;
  canDeleteUser: boolean;
  canEditRole: boolean;
  canCreateRole: boolean;
  canDeleteRole: boolean;
  canEditZone: boolean;
  canCreateZone: boolean;
  canDeleteZone: boolean;
}

export interface AdminRefs {
  roles: Array<{ code: string; name: string; rank: number; description: string | null }>;
  zones: Array<{ code: string; name: string; depth: number; path: string }>;
  branches: Array<{ code: string; name: string; isHeadOffice: boolean }>;
  warehouses: Array<{ code: string; name: string }>;
  managers: Array<{ email: string; fullName: string }>;
  scopeLevels: string[];
  statuses: string[];
  caller: AdminCaller;
}

export interface AdminWriteResult {
  message: string;
  oneTimePassword?: string;
  username?: string;
}


export interface CompanyProfile {
  code: string;
  name: string;
  legalName: string;
  tagline: string | null;
  brandMark: string | null;
  logoUrl: string | null;
  ntn: string | null;
  strn: string | null;
  cuin: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  province: string | null;
  countryCode: string;
  phone: string | null;
  email: string | null;
  website: string | null;
  baseCurrency: string;
  valuationMethod: string;
  timezone: string;
  fyStartMonth: number;
  fyStartDay: number;
  allowNegativeStock: boolean;
  enforceCreditLimit: boolean;
  makerCheckerEnabled: boolean;
  batchTracking: boolean;
  defaultBatchPick: string;
  defaultUomCode: string | null;
  reportFooter: string | null;
  printShowNtn: boolean;
  printShowStrn: boolean;
  printShowCuin: boolean;
  isActive: boolean;
  hasPostings: boolean;
  hasStockMovement: boolean;
}

export interface FiscalYearRow {
  id: string;
  code: string;
  name: string;
  startDate: string;
  endDate: string;
  status: string;
  closedAt: string | null;
  closedByName: string | null;
  periodCount: number;
  voucherCount: number;
}

export interface PeriodRow {
  id: string;
  fiscalYearId: string;
  fiscalYearCode: string;
  periodNo: number;
  name: string;
  startDate: string;
  endDate: string;
  status: string;
  isAdjustment: boolean;
  closedAt: string | null;
  closedByName: string | null;
  reopenedAt: string | null;
  reopenedByName: string | null;
  reopenReason: string | null;
  postedCount: number;
  draftCount: number;
}

export interface NumberSeriesRow {
  id: string;
  docType: string;
  prefix: string;
  separator: string;
  yearToken: string | null;
  padding: number;
  nextValue: string;
  isGapless: boolean;
  isActive: boolean;
  fiscalYearCode: string | null;
  branchCode: string | null;
  branchName: string | null;
  nextNumber: string;
  issued: number;
}

export interface TaxCodeRow {
  id: string;
  code: string;
  name: string;
  kind: string;
  computation: string;
  rate: string;
  fixedPerUnit: string | null;
  uomCode: string | null;
  appliesToFiler: boolean;
  nonFilerRate: string | null;
  accountCode: string | null;
  accountName: string | null;
  isWithholding: boolean;
  isRecoverable: boolean;
  statutorySection: string | null;
  effectiveFrom: string;
  effectiveTo: string | null;
  isActive: boolean;
  documentCount: number;
}

export interface FxRateRow {
  id: string;
  rateDate: string;
  fromCurrency: string;
  toCurrency: string;
  rateType: string;
  rate: string;
  source: string | null;
  note: string | null;
  createdByName: string | null;
}

export interface BranchRow {
  id: string;
  code: string;
  name: string;
  addressLine1: string | null;
  city: string | null;
  phone: string | null;
  isHeadOffice: boolean;
  isActive: boolean;
  userCount: number;
  voucherCount: number;
  warehouseCount: number;
  seriesCount: number;
}

export interface CurrencyRow {
  id: string;
  code: string;
  name: string;
  symbol: string | null;
  decimals: number;
  isBase: boolean;
  isActive: boolean;
  voucherCount: number;
  rateCount: number;
  accountCount: number;
  latestRate: string | null;
}

export interface UomRow {
  id: string;
  code: string;
  name: string;
  dimension: string;
  decimals: number;
  factorToBase: string;
  isActive: boolean;
  baseForProducts: number;
  usedByProducts: number;
  conversionCount: number;
  movementCount: number;
}

export interface SetupRefs {
  currencies: Array<{ code: string; name: string; symbol: string | null; isBase: boolean }>;
  branches: Array<{ code: string; name: string; isHeadOffice: boolean }>;
  fiscalYears: Array<{ code: string; name: string; status: string }>;
  uoms: Array<{ code: string; name: string; dimension: string }>;
  accounts: Array<{ code: string; name: string }>;
}


export interface BankAccountOption {
  code: string;
  label: string;
  bankName: string;
  accountNumber: string | null;
  currency: string;
  isDefault: boolean;
}

export interface ReconLine {
  lineNo: number;
  txnDate: string;
  valueDate: string | null;
  description: string;
  reference: string | null;
  instrumentNo: string | null;
  debit: string;
  credit: string;
  signed: string;
  runningBalance: string | null;
  status: string;
  matchedVoucherNo: string | null;
  matchConfidence: string | null;
  ignoreReason: string | null;
}

export interface ReconciliationView {
  accounts: BankAccountOption[];
  account: BankAccountOption | null;
  statement: {
    id: string;
    statementNo: string | null;
    fromDate: string;
    toDate: string;
    openingBalance: string;
    closingBalance: string;
    sourceFormat: string;
    sourceFileName: string | null;
    importedAt: string;
    importedBy: string | null;
  } | null;
  counts: {
    total: number;
    matched: number;
    suggested: number;
    unmatched: number;
    ignored: number;
    open: number;
  };
  totals: {
    statementBalance: string;
    ledgerBalance: string;
    difference: string;
    debitTotal: string;
    creditTotal: string;
  };
  lines: ReconLine[];
  lastReconciled: { date: string; balance: string; status: string } | null;
}

export interface NavCounts {
  recon: number;
  settlement: number;
}


export interface AccountLinkRow {
  key: string;
  label: string;
  posts: string;
  side: string;
  isControl: boolean;
  use: 'POSTS' | 'DEFAULT' | 'UNUSED';
  accountType: string[];
  code: string | null;
  name: string | null;
  level: number | null;
  isLeaf: boolean | null;
  isActive: boolean | null;
  unpostable: boolean;
}

export interface AccountLinkGroup {
  module: string;
  label: string;
  note: string | null;
  links: AccountLinkRow[];
}

export interface AccountLinks {
  groups: AccountLinkGroup[];
  missing: string[];
}

export interface AccountLinkWriteResult {
  message: string;
  key: string;
  code: string;
  name: string;
  previousCode: string | null;
}


export function useCan(session: Session | undefined) {
  return useMemo(() => {
    const held = new Set(session?.permissions ?? []);
    return (module: string, action = 'VIEW') =>
      Boolean(session?.isSuperAdmin) || held.has(`${module}:${action}`);
  }, [session]);
}
